"use client";

import { openDrawer, useCartCount } from "@/lib/cart-store";

/**
 * Cart trigger button mounted in the Masthead.
 *
 * Subscribes to useCartCount() so the badge updates live as lines are
 * added/removed from any consumer (ProductActions quick-add, /cart page,
 * cross-tab storage event). Renders a 44x44 tap target for mobile WCAG.
 *
 * Click delegates to openDrawer() — the actual drawer lives once in
 * app/layout.tsx so this button is purely a trigger.
 */
export function CartButton() {
  const count = useCartCount();
  const label = count > 0
    ? `Open bag — ${count} ${count === 1 ? "item" : "items"}`
    : "Open bag";

  return (
    <button
      type="button"
      onClick={openDrawer}
      aria-label={label}
      className="relative inline-flex h-11 w-11 items-center justify-center rounded-sm text-[var(--color-bone)] transition hover:text-[var(--color-foil)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
    >
      <BagIcon className="h-5 w-5" />
      {count > 0 && (
        <span
          aria-hidden="true"
          className="absolute -right-0.5 -top-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--color-blood)] px-1 font-mono text-[10px] font-bold leading-none text-white"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

function BagIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M5 8h14l-1 11.5a2 2 0 0 1-2 1.8H8a2 2 0 0 1-2-1.8L5 8z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M9 8V6a3 3 0 1 1 6 0v2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
